import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { VelavuDevice, VelavuFloor } from "velavu-js-api";
import Divider from "../../../../../elements/divider";
import DynamicBattery from "../../../../../elements/dynamic-battery";
import SelectableCard from "../../../../../elements/selectable-card";
import StatusIndicator from "../../../../../elements/status-indicator";
import { getLocationFloorID } from "../../../../../helper/floor-helper";
import { getDeviceStatus } from "../../../../../helper/status-helper";
import DevicesFilter from "../devices-filter/devices-filter";
import * as styles from "./site-detail-gateways.module.scss";
import FloorFilter from "../../../../../data/floor-filter";
import DevicesToolbar from "../devices-toolbar/devices-toolbar";
import { PinnedCascadingPanelStackPortal } from "../../../../../elements/cascading-panel-stack";
import GatewaySelectPanel from "./gateway-select-panel";
import useMenuAnchorState from "../../../../../state/menu-anchor-state";
import { useHasPermission } from "../../../../sign-in-context/sign-in-context";

interface SiteDetailGatewaysProps {
	siteID: string;
	gateways: VelavuDevice[] | undefined;
	onSelectGateway?: (deviceID: string) => void;
	onHoverGateway?: (deviceID: string | undefined) => void;
	filterTargetFloor?: VelavuFloor;
	onChangeFloorFilter: (floor: FloorFilter) => void;
	onPinNewGateway?: (gateway: VelavuDevice) => void;
	showFloorFilter?: boolean;
}

function Gateway(props: { gateway: VelavuDevice }) {
	return (
		<>
			<StatusIndicator
				status={getDeviceStatus(props.gateway)}
				className={styles.indicator}
			/>
			<span className={styles.gatewayName}>
				{props.gateway.asset?.name ?? props.gateway.id}
			</span>
			<div className={styles.battery}>
				<span
					style={{ marginRight: 4 }}
					className={styles.batteryLevel}
				>
					{props.gateway.state?.power?.battery_level !== undefined
						? `${props.gateway.state.power.battery_level}%`
						: "N/A"}
				</span>
				<DynamicBattery
					level={
						props.gateway.state?.power?.battery_level !== undefined
							? props.gateway.state?.power.battery_level / 100
							: -1
					}
					charging={
						props.gateway.state !== undefined &&
						"usb_power" in props.gateway.state.power &&
						props.gateway.state.power.usb_power
					}
				/>
			</div>
		</>
	);
}

export default function SiteDetailGateways(props: SiteDetailGatewaysProps) {
	const { t } = useTranslation();

	const canWriteDevices = useHasPermission("devices:write");

	const [searchText, setSearchText] = useState("");
	const [showFilterMenu, setShowFilterMenu] = useState(false);
	const [filterBattery, setFilterBattery] = useState<boolean | undefined>(
		undefined,
	);
	const [filterStatus, setFilterStatus] = useState<boolean | undefined>(
		undefined,
	);
	const toggleFilterMenu = useCallback(
		() => setShowFilterMenu((it) => !it),
		[setShowFilterMenu],
	);

	const filteredGateways = useMemo(() => {
		let gateways = props.gateways ?? [];

		if (filterStatus !== undefined) {
			gateways = gateways.filter(
				(gateway) => gateway.online === filterStatus,
			);
		}

		if (filterBattery !== undefined) {
			gateways = gateways.filter(
				(gateway) =>
					gateway.state?.power?.battery_level &&
					gateway.state?.power?.battery_level < 15,
			);
		}

		gateways = gateways.filter((gateway) =>
			gateway.id.toLowerCase().includes(searchText.toLowerCase()),
		);

		const filterTargetFloor = props.filterTargetFloor;
		if (filterTargetFloor !== undefined)
			gateways = gateways.filter(
				(g) => getLocationFloorID(g?.location) === filterTargetFloor.id,
			);

		return gateways;
	}, [
		props.filterTargetFloor,
		props.gateways,
		searchText,
		filterStatus,
		filterBattery,
	]);

	const [menuPinAnchor, openPinMenu, closePinMenu] = useMenuAnchorState();

	return (
		<>
			{menuPinAnchor && (
				<PinnedCascadingPanelStackPortal refPosition={menuPinAnchor}>
					<GatewaySelectPanel
						onClose={closePinMenu}
						siteID={props.siteID}
						onSelect={props.onPinNewGateway}
					/>
				</PinnedCascadingPanelStackPortal>
			)}

			<div className={styles.gateways}>
				<DevicesToolbar
					searchText={searchText}
					setSearchText={setSearchText}
					levelSelection={props.filterTargetFloor?.level}
					onChangeFloor={props.onChangeFloorFilter}
					showFloorFilter={props.showFloorFilter}
					highlightFilter={
						filterBattery !== undefined ||
						filterStatus !== undefined
					}
					showFilterMenu={showFilterMenu}
					onClickShowFilterMenu={toggleFilterMenu}
					onClickAdd={canWriteDevices ? openPinMenu : undefined}
					addFocused={menuPinAnchor !== null}
				/>
				<Divider />
				{showFilterMenu && (
					<>
						<DevicesFilter
							filterStatus={filterStatus}
							selectStatus={setFilterStatus}
							filterBattery={filterBattery}
							selectBattery={setFilterBattery}
						/>
						<Divider />
					</>
				)}
				<div className={styles.content}>
					{filteredGateways.length > 0 ? (
						filteredGateways.map((gateway) => (
							<SelectableCard
								key={gateway.id}
								className={styles.gatewayCard}
								onClick={() => {
									props.onSelectGateway?.(gateway.id);
									props.onHoverGateway?.(undefined);
								}}
								onMouseEnter={() =>
									props.onHoverGateway?.(gateway.id)
								}
								onMouseLeave={() =>
									props.onHoverGateway?.(undefined)
								}
							>
								<Gateway gateway={gateway} />
							</SelectableCard>
						))
					) : (
						<div className={styles.emptyLabel}>
							{t("common.no_results")}
						</div>
					)}
				</div>
			</div>
		</>
	);
}
